// The modal (design manual Ch08 §4.2): one overlay, one panel, a header with
// the title and a close control, a BODY WITH PADDING, and an optional footer
// for the actions. Before 2026-09-21 six files carried a shell with a padded
// header and an unpadded body, so content sat flush against the panel's edge
// (UAT: "need more interior padding in modals, universal style fix"); six more
// drew their own overlay and panel. This is the one place a modal is drawn.
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Button } from './Button';

export interface ModalProps {
  title: ReactNode;
  /** A line under the title: what the modal is for, in one sentence. */
  subtitle?: ReactNode;
  onClose: () => void;
  /** While an action runs, the overlay and the close control stay put. */
  busy?: boolean;
  /** `lg` is the default; `xl` for a report or a table. */
  size?: 'md' | 'lg' | 'xl';
  /** The action row. Cancel on the left, the primary or danger action last. */
  footer?: ReactNode;
  /** A destructive modal draws its border in the error colour. */
  danger?: boolean;
  titleId?: string;
  children: ReactNode;
  className?: string;
}

const SIZE = { md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-3xl' };

export function Modal({ title, subtitle, onClose, busy = false, size = 'lg', footer, danger = false, titleId, children, className }: ModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className={cn(
          'flex max-h-[90vh] w-full flex-col rounded-lg border bg-card text-tres-text-primary shadow-xl',
          danger ? 'border-tres-error' : 'border-border',
          SIZE[size],
          className,
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className={cn('text-lg font-semibold', danger ? 'text-tres-error' : 'text-tres-text-primary')}>
              {title}
            </h2>
            {subtitle && <p className="mt-0.5 text-xs text-tres-text-secondary">{subtitle}</p>}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={busy} aria-label="Close">
            ✕
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-border px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}
